/**
 * BillingBanner: the one place the whole portal tells you about money owed.
 *
 * - Trial with three days or fewer left: what the first bill will be.
 * - Renewal: inside the notice window (a school's term 14 days, a year 30;
 *   a rental month 7, a year 30) "Subscription ends in N days" and Renew
 *   Now. Details are on Plan & billing.
 * - An invoice in its pay window: full access, with its due date and Pay.
 * - Past due: read-only until paid (Rule 3), with the amount and Pay.
 * - Suspended: the same, in red.
 *
 * Paying opens Paystack in the same tab (Rule 4). Staff who cannot manage
 * billing are told who can.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, useAuthStore, usePermission } from '@lipaflow/shared'
import toast from 'react-hot-toast'
import { isDemoTenant } from '../../lib/onboardingRouting'
import { apiErrorDetail, billingService, formatMoney, type RenewalSummary } from '../../services/billing'

interface Overview {
  currency: string
  subscription: { status: string; is_trial: boolean; trial_days_remaining: number | null } | null
  renewal?: RenewalSummary | null
  recent_invoices: Array<{
    id: string
    number: string
    status: string
    remaining_amount: number
    invoice_type: string
    due_date: string | null
  }>
}

const OUTSTANDING = new Set(['open', 'partial', 'overdue'])
const TRIAL_NOTICE_DAYS = 3

/** Time left to ``ends``, short: "4 days", "3 h 20 min", "today". Ticks each minute. */
export function useCountdown(ends: string | null): string | null {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])
  if (!ends) return null
  const left = new Date(ends).getTime() - now
  if (left <= 0) return 'today'
  const days = Math.floor(left / 86_400_000)
  if (days >= 1) return `in ${days} day${days === 1 ? '' : 's'}`
  const hours = Math.floor(left / 3_600_000)
  const minutes = Math.floor((left % 3_600_000) / 60_000)
  return hours > 0 ? `in ${hours} h ${minutes} min` : `in ${minutes} min`
}

/** The renewal countdown: "Subscription ends in N days" and Renew Now. Paying
 *  the raised renewal renews; otherwise Renew Now opens Plan & billing (to
 *  carry on a plan set to stop). Details live there. */
function RenewalCountdown({ renewal, currency, canPay, onPay, paying }: {
  renewal: RenewalSummary
  currency: string
  canPay: boolean
  onPay: (invoiceId: string) => void
  paying: boolean
}) {
  const left = useCountdown(renewal.ends_at)
  const invoice = renewal.cancelling ? null : renewal.invoice
  const button = 'rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-60'
  return (
    <div role="status" className="bg-teal-50 border-b border-teal-200 px-4 py-2 flex flex-wrap items-center justify-between gap-3 text-sm text-teal-900">
      <p className="font-semibold" data-testid="renewal-countdown">Subscription ends {left}</p>
      {!canPay ? (
        <span className="text-xs">Your administrator can renew it.</span>
      ) : invoice ? (
        <button
          type="button"
          onClick={() => onPay(invoice.id)}
          disabled={paying}
          aria-label={`Renew Now: pay ${formatMoney(invoice.remaining, currency)}`}
          className={button}
        >
          {paying ? 'Opening payment…' : 'Renew Now'}
        </button>
      ) : (
        <Link to="/subscriptions" className={button}>Renew Now</Link>
      )}
    </div>
  )
}

export default function BillingBanner() {
  const tenant = useAuthStore((s) => s.tenant)
  const isDemo = isDemoTenant(tenant)
  const canPay = usePermission('settings:manage')
  // The overview needs settings:view; asking without it was a 403 on every page.
  const canView = usePermission('settings:view')

  const { data } = useQuery<Overview>({
    queryKey: ['subscription-overview'],
    queryFn: async () => (await api.get('/subscriptions/overview')).data,
    enabled: !isDemo && canView,
    retry: false,
    staleTime: 60_000,
  })
  const subscription = data?.subscription
  const trialEndingSoon = Boolean(
    subscription?.is_trial &&
      subscription.trial_days_remaining !== null &&
      subscription.trial_days_remaining <= TRIAL_NOTICE_DAYS,
  )
  const { data: quote } = useQuery({
    queryKey: ['billing-quote'],
    queryFn: () => billingService.getQuote(),
    enabled: trialEndingSoon,
    retry: false,
  })

  const pay = useMutation({
    mutationFn: (invoiceId: string) => billingService.payInvoice(invoiceId),
    onSuccess: (result) => {
      window.location.href = result.authorization_url
    },
    onError: (err) => toast.error(apiErrorDetail(err, 'Could not start the payment')),
  })

  if (isDemo || !subscription) return null
  const currency = data?.currency ?? 'KES'
  const owed = (data?.recent_invoices ?? []).find(
    (invoice) => OUTSTANDING.has(invoice.status) && invoice.invoice_type !== 'addon',
  )

  if (subscription.status === 'past_due' || subscription.status === 'suspended') {
    const suspended = subscription.status === 'suspended'
    return (
      <div
        role="alert"
        className={`${suspended ? 'bg-error-50 border-error-200 text-error-900' : 'bg-warning-50 border-warning-200 text-warning-900'} border-b px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 text-sm`}
      >
        <p>
          <span className="font-semibold">{owed ? `Invoice ${owed.number} is unpaid.` : 'Your account has an unpaid invoice.'}</span>{' '}
          {suspended
            ? 'Your account is suspended until it is paid.'
            : 'You can view everything, but changes and messages are paused until it is paid.'}
        </p>
        {owed && canPay ? (
          <button
            type="button"
            onClick={() => pay.mutate(owed.id)}
            disabled={pay.isPending}
            className="rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-60"
          >
            {pay.isPending ? 'Opening payment…' : `Pay ${formatMoney(owed.remaining_amount, currency)}`}
          </button>
        ) : (
          <span className="text-xs">Ask your administrator to pay it.</span>
        )}
      </div>
    )
  }

  // The renewal countdown, inside its notice window: everyone sees it, the
  // way to pay only those who can.
  const renewal = data?.renewal
  if (subscription.status === 'active' && renewal?.showing) {
    return (
      <RenewalCountdown
        renewal={renewal}
        currency={currency}
        canPay={canPay}
        onPay={(id) => pay.mutate(id)}
        paying={pay.isPending}
      />
    )
  }

  // A routine "invoice due" note is for whoever pays it; staff who can't only
  // need to know when the account is paused (above).
  if (subscription.status === 'active' && owed && canPay) {
    const due = owed.due_date
      ? new Date(owed.due_date).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })
      : null
    return (
      <div role="status" className="bg-teal-50 border-b border-teal-200 px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 text-sm text-teal-900">
        <p>
          <span className="font-semibold">Invoice {owed.number}{due ? ` is due on ${due}` : ' is open'}.</span>{' '}
          Everything keeps working until then.
        </p>
        {canPay ? (
          <button
            type="button"
            onClick={() => pay.mutate(owed.id)}
            disabled={pay.isPending}
            className="rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-60"
          >
            {pay.isPending ? 'Opening payment…' : `Pay ${formatMoney(owed.remaining_amount, currency)}`}
          </button>
        ) : (
          <span className="text-xs">Your administrator can pay it.</span>
        )}
      </div>
    )
  }

  if (trialEndingSoon && quote && quote.amount !== null && quote.count > 0) {
    const days = subscription.trial_days_remaining ?? 0
    return (
      <div role="status" className="bg-teal-50 border-b border-teal-200 px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 text-sm text-teal-900">
        <p>
          <span className="font-semibold">Trial: {days <= 0 ? 'ends today' : `${days} day${days === 1 ? '' : 's'} left`}.</span>{' '}
          Your first bill: {formatMoney(quote.amount, quote.currency)} for {quote.count.toLocaleString()} {quote.unit_label}.
        </p>
        {canPay && (
          <Link to="/subscriptions" className="font-medium underline">
            See how it's worked out
          </Link>
        )}
      </div>
    )
  }
  return null
}
